import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LifeBuoy, Send, CheckCircle2 } from "lucide-react";
import { API } from "@/lib/api";
import { useBotGuard } from "@/lib/botGuard";

const pub = axios.create({ baseURL: API });

const KIND_LABEL = {
  bug:      "Something's broken",
  question: "A question",
  feature:  "An idea / feature",
  other:    "Other",
};

export default function SupportDialog({ children }) {
  const { withGuard, Honeypot } = useBotGuard();
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", kind: "question", subject: "", message: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (form.message.length < 10) {
      toast.error("Tell us a bit more — at least 10 characters.");
      return;
    }
    setBusy(true);
    try {
      await pub.post("/public/support", withGuard(form));
      setSubmitted(true);
    } catch (err) {
      const detail = err.response?.data?.detail || "Could not send";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setSubmitted(false);
    setForm({ name: "", email: "", kind: "question", subject: "", message: "" });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setTimeout(reset, 200); }}>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 text-stone-300 hover:text-amber-300 transition-colors"
        data-testid="footer-support-link"
      >
        <LifeBuoy className="w-3.5 h-3.5" /> {children || "Support"}
      </button>

      <DialogContent className="max-w-md" data-testid="support-dialog">
        {submitted ? (
          <div className="text-center py-2" data-testid="support-success">
            <div className="w-12 h-12 rounded-full bg-emerald-100 grid place-items-center mx-auto mb-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-700" />
            </div>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-medium">Got it — thanks!</DialogTitle>
              <DialogDescription className="text-stone-500 mt-1">
                We've logged your request. A human will look at it shortly and reply to <span className="font-medium text-stone-700">{form.email}</span>.
              </DialogDescription>
            </DialogHeader>
            <Button onClick={() => setOpen(false)} className="rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300 mt-5" data-testid="support-close">
              Close
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-medium">How can we help?</DialogTitle>
              <DialogDescription className="text-stone-500">
                A bug, a question, or a feature wish — we read every one.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3 mt-2" data-testid="support-form">
              <Honeypot />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Name</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Anna"
                    required maxLength={120}
                    className="mt-1"
                    data-testid="support-name"
                  />
                </div>
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="anna@example.com"
                    required
                    className="mt-1"
                    data-testid="support-email"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger className="mt-1" data-testid="support-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(KIND_LABEL).map(([k, label]) => (
                      <SelectItem key={k} value={k}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Subject</Label>
                <Input
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  placeholder="Couldn't add a photo to my journal"
                  required minLength={3} maxLength={160}
                  className="mt-1"
                  data-testid="support-subject"
                />
              </div>
              <div>
                <Label className="text-xs">Details</Label>
                <Textarea
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="What did you try, what did you expect, what happened?"
                  required minLength={10} maxLength={4000}
                  className="mt-1"
                  data-testid="support-message"
                />
                <div className="text-[11px] text-stone-400 mt-1">{form.message.length} / 4000</div>
              </div>
              <Button
                type="submit"
                disabled={busy}
                className="w-full rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300"
                data-testid="support-submit"
              >
                <Send className="w-4 h-4 mr-1.5" /> {busy ? "Sending…" : "Send request"}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
